// Web Vitals tracking via the native PerformanceObserver API.
// Measures CLS, FCP, FID, LCP, and TTFB without any npm package dependency.

import { phTrack } from './posthog';

// ─── Types ────────────────────────────────────────────────────────────────────

interface VitalMetric {
  name: 'CLS' | 'FCP' | 'FID' | 'LCP' | 'TTFB';
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
}

// ─── Thresholds ───────────────────────────────────────────────────────────────

const THRESHOLDS: Record<string, [good: number, poor: number]> = {
  LCP:  [2500, 4000],
  FID:  [100,  300],
  CLS:  [0.1,  0.25],
  FCP:  [1800, 3000],
  TTFB: [800,  1800],
};

function getRating(name: string, value: number): 'good' | 'needs-improvement' | 'poor' {
  const thresholds = THRESHOLDS[name];
  if (!thresholds) return 'needs-improvement';
  const [good, poor] = thresholds;
  if (value <= good) return 'good';
  if (value > poor)  return 'poor';
  return 'needs-improvement';
}

// ─── Reporting ────────────────────────────────────────────────────────────────

function reportVital(metric: VitalMetric): void {
  const { name, value, rating } = metric;

  phTrack('web_vital_measured', { name, value, rating });
}

// ─── Observers ────────────────────────────────────────────────────────────────

/** Observe Largest Contentful Paint. Reports the last entry before input/scroll. */
function observeLCP(): void {
  try {
    let reported = false;
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const last = entries[entries.length - 1];
      if (!last || reported) return;

      const value = last.startTime;
      reported = true;
      observer.disconnect();
      reportVital({ name: 'LCP', value, rating: getRating('LCP', value) });
    });
    observer.observe({ type: 'largest-contentful-paint', buffered: true });
  } catch {
    // PerformanceObserver may not support this entry type in all browsers.
  }
}

/** Observe First Input Delay. */
function observeFID(): void {
  try {
    let reported = false;
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      for (const entry of entries) {
        if (reported) break;
        // PerformanceEventTiming has processingStart; cast via unknown.
        const eventEntry = entry as unknown as { processingStart: number; startTime: number };
        const value = eventEntry.processingStart - eventEntry.startTime;
        reported = true;
        observer.disconnect();
        reportVital({ name: 'FID', value, rating: getRating('FID', value) });
      }
    });
    observer.observe({ type: 'first-input', buffered: true });
  } catch {
    // Silently skip if unsupported.
  }
}

/** Observe Cumulative Layout Shift. Accumulates all non-input-triggered shifts. */
function observeCLS(): void {
  try {
    let clsValue = 0;
    let reported = false;

    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        // LayoutShift entries have hadRecentInput and value; cast via unknown.
        const shift = entry as unknown as { hadRecentInput: boolean; value: number };
        if (!shift.hadRecentInput) {
          clsValue += shift.value;
        }
      }
    });

    observer.observe({ type: 'layout-shift', buffered: true });

    // Report CLS when the page is hidden (navigation away, tab switch, etc.).
    const reportCLS = () => {
      if (reported) return;
      reported = true;
      observer.disconnect();
      reportVital({ name: 'CLS', value: clsValue, rating: getRating('CLS', clsValue) });
    };

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') reportCLS();
    }, { once: true });

    // Also report on pagehide for bfcache-eligible pages.
    window.addEventListener('pagehide', reportCLS, { once: true });
  } catch {
    // Silently skip if unsupported.
  }
}

/** Observe First Contentful Paint via the 'paint' entry type. */
function observeFCP(): void {
  try {
    let reported = false;
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.name === 'first-contentful-paint' && !reported) {
          const value = entry.startTime;
          reported = true;
          observer.disconnect();
          reportVital({ name: 'FCP', value, rating: getRating('FCP', value) });
          break;
        }
      }
    });
    observer.observe({ type: 'paint', buffered: true });
  } catch {
    // Silently skip if unsupported.
  }
}

/** Measure Time to First Byte from the navigation timing entry. */
function measureTTFB(): void {
  try {
    const entries = performance.getEntriesByType('navigation');
    if (!entries.length) return;

    const nav = entries[0] as PerformanceNavigationTiming;
    const value = nav.responseStart - nav.requestStart;
    if (value < 0) return; // Guard against invalid values.

    reportVital({ name: 'TTFB', value, rating: getRating('TTFB', value) });
  } catch {
    // Silently skip if unsupported.
  }
}

// ─── Entry point ──────────────────────────────────────────────────────────────

/** Initialise all Web Vital observers. Safe to call multiple times, guards internally. */
export function initWebVitals(): void {
  if (typeof window === 'undefined' || !window.PerformanceObserver) return;

  observeLCP();
  observeFID();
  observeCLS();
  observeFCP();
  measureTTFB();
}
